import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { setCookie } from 'nookies';
import { api, saveAuthData, unwrapApiResponse } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/Input';

export default function RegisterPage() {
    const navigate = useNavigate();
    const { login: authLogin } = useAuth();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.username.trim()) newErrors.username = 'Nome de usuário é obrigatório';
        if (!formData.email.trim()) newErrors.email = 'Email é obrigatório';
        if (!formData.password) newErrors.password = 'Senha é obrigatória';
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'As senhas não coincidem';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        setErrors({});
        try {
            await api.post('usuarios', {
                email: formData.email.trim(),
                senha: formData.password,
                senha_confirmacao: formData.confirmPassword,
                nome: formData.username.trim()
            }, { requiresAuth: false });

            const loginRes = await api.post('auth/login', {
                email: formData.email.trim(),
                senha: formData.password
            }, { requiresAuth: false });

            const loginData = unwrapApiResponse<{ token?: string; access_token?: string }>(loginRes);
            const token = loginData.token || loginData.access_token;
            if (token) {
                setCookie(null, 'token', token, { maxAge: 60 * 60 * 24 * 7, path: '/' });
                const userData = unwrapApiResponse<any>(await api.get('usuarios/me', { requiresAuth: true }));
                saveAuthData(token, userData);
                authLogin(token, userData);
                const hasVip = !!userData.plan?.isActive;
                navigate(hasVip ? '/' : '/landing');
            } else {
                navigate('/entrar');
            }
        } catch (err: any) {
            setErrors({ email: err.message || 'Erro ao criar conta' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-[#0a0a0a] pt-10 pb-10 px-6 flex items-center justify-center relative overflow-hidden">
            <div
                className="absolute top-0 left-0 w-96 h-96 md:w-[600px] md:h-[600px] pointer-events-none"
                style={{
                    maskImage: "radial-gradient(ellipse 100% 100% at 0% 0%, black 50%, transparent 85%)",
                    WebkitMaskImage: "radial-gradient(ellipse 100% 100% at 0% 0%, black 50%, transparent 85%)",
                }}
            >
                <img src="/wave2.png" alt="" aria-hidden className="w-full h-full object-contain opacity-20" />
            </div>
            <div className="w-full max-w-md relative z-10">
                <div className="text-center mb-10">
                    <h1 className="text-4xl md:text-5xl font-black mb-3 bg-clip-text text-transparent bg-gradient-to-r from-orange-400 via-orange-500 to-red-600 tracking-tight">Criar Conta</h1>
                    <p className="text-lg text-gray-300 font-medium">Junte-se ao mundo mediocre</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Nome de Usuário</label>
                        <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                            <Input type="text" name="username" value={formData.username} onChange={handleChange} className="rounded-lg py-3 pl-12 pr-4" placeholder="Digite seu nome de usuário" required />
                        </div>
                        {errors.username && <p className="mt-1 text-sm text-red-400">{errors.username}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                            <Input type="email" name="email" value={formData.email} onChange={handleChange} className="rounded-lg py-3 pl-12 pr-4" placeholder="Digite seu email" required />
                        </div>
                        {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Senha</label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                            <Input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} className="rounded-lg py-3 pl-12 pr-12" placeholder="Digite sua senha" required />
                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">{showPassword ? <EyeOff /> : <Eye />}</button>
                        </div>
                        {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Confirmar Senha</label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                            <Input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="rounded-lg py-3 pl-12 pr-12" placeholder="Confirme sua senha" required />
                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">{showConfirmPassword ? <EyeOff /> : <Eye />}</button>
                        </div>
                        {errors.confirmPassword && <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>}
                    </div>
                    <button type="submit" disabled={loading} className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-lg transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">{loading ? 'Criando conta...' : 'Criar Conta'}</button>
                </form>

                <div className="mt-6 text-center">
                    <p className="text-gray-400 text-sm">Já tem uma conta? <Link to="/entrar" className="text-orange-500 hover:text-orange-400 font-medium">Entrar</Link></p>
                </div>
            </div>
        </main>
    );
}
